// Is the page being PRINTED right now?
//
// Print pagination is the one thing the screen sheet cannot do for itself: an
// engraved system is ONE SVG and a chart row is a slice of ONE grid, so no
// page break can fall inside either — the browser pushes a whole unbreakable
// box to the next page, which is how a masthead ended up alone on page 1.
// The fix is to hand the printer the same music cut into blocks it MAY break
// between (one per system, one per chart row). Rendering those blocks costs a
// full second copy of the sheet, so they are mounted only while printing, and
// this module is the signal.
//
// Two facts decide the shape:
//  - `beforeprint` is the LAST moment before the browser takes its snapshot,
//    so the state change must land SYNCHRONOUSLY (flushSync) — React's normal
//    async commit arrives after the paper is drawn.
//  - The print MEDIA QUERY is the second, independent source: Playwright's
//    `emulateMedia({media:'print'})` fires it and never fires `beforeprint`,
//    and a browser that skips `beforeprint` still flips the query. Either one
//    turning off ends print mode, so a stuck flag needs both to fail.
//
// Nothing here can strand the app: the print sheet is `display: none` on
// screen and the flowing sheet is hidden only inside `@media print`, so the
// flag being wrong in either direction shows the reader exactly what they see
// today.

import { useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';

let printing = false;
let installed = false;
const listeners = new Set<() => void>();

function setPrinting(next: boolean) {
  if (printing === next) return;
  printing = next;
  // Outside React's own work loop (a browser event), so this commits now.
  flushSync(() => {
    listeners.forEach((listener) => listener());
  });
}

function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('beforeprint', () => setPrinting(true));
  window.addEventListener('afterprint', () => setPrinting(false));
  const query = typeof window.matchMedia === 'function' ? window.matchMedia('print') : null;
  if (!query) return;
  // Already in print media when the first consumer mounts (emulated media, a
  // print preview that outlived a reload): useSyncExternalStore re-reads the
  // snapshot right after subscribing, so setting it here is seen.
  if (query.matches) printing = true;
  query.addEventListener?.('change', (event) => setPrinting(event.matches));
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  install();
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: the module is a page-lifetime singleton, so a suite that
 * dispatches print events needs the flag back at rest between cases. */
export function __resetPrintModeForTests() {
  printing = false;
  installed = false;
  listeners.clear();
}

export function usePrintMode(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => printing,
    () => false,
  );
}
